import {
  buildSurveySubmitBodyFromStorage,
  submitCustomerSurveyTng,
} from '@/api/customerSurvey';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { isQ7_1Complete } from '@/lib/q7_1Storage';
import { isQ7_2Complete } from '@/lib/q7_2Storage';
import { isQ7_3Complete } from '@/lib/q7_3Storage';
import { isQ7_4CompleteForSurvey } from '@/lib/q7_4Storage';
import { isDevRegComplete } from '@/lib/devregStorage';
import { clearAllLocalStorage } from '@/lib/clearAllLocalStorage';
import { writeTngRewardSession } from '@/lib/tngRewardSession';
import {
  type TradeInAnswer,
  readTradeInAnswers,
  writeTradeInAnswers,
} from '@/lib/tradeinStorage';
import { isSmokerProfileComplete } from '@/lib/smokerProfileStorage';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppSelector } from '@/stores/hooks';
import { selectPdpaConsent, selectSurveyScreening, selectUniqueCodeDraft } from '@/stores/selectors';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

type TradeInOption = { id: TradeInAnswer; labelKey: 'optionYesHelp' | 'optionNotInterested' | 'optionAlreadyNew' };
const OPTION_KEYS: TradeInOption[] = [
  { id: 'yes_help', labelKey: 'optionYesHelp' },
  { id: 'not_interested', labelKey: 'optionNotInterested' },
  { id: 'already_new', labelKey: 'optionAlreadyNew' },
];

const checkboxLabelClass =
  'flex cursor-pointer items-start gap-3 text-left text-[15px] font-medium leading-snug text-white/95 sm:text-[16px]';

const checkboxClass =
  'mt-0.5 h-[18px] w-[18px] shrink-0 rounded border border-slate-300/90 bg-white text-amber-800 focus:ring-1 focus:ring-slate-300';

/** Placeholders for API validation (trade-in contact form is skipped on this path). */
const TNG_SKIP_CONTACT = {
  name: 'Participant',
  email: 'participant@survey.invalid',
} as const;

export default function TradeIn() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { mobileLocalDigits, customerUuid } = useAppSelector(selectUniqueCodeDraft);

  const [choice, setChoice] = useState<TradeInAnswer | null>(
    () => readTradeInAnswers()?.tradeIn ?? null
  );
  const [savingTng, setSavingTng] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const pick = (v: TradeInAnswer) => {
    if (savingTng) return;
    if (v === 'yes_help' && choice === v) return;
    setSaveError(null);
    writeTradeInAnswers({ tradeIn: v });
    setChoice(v);

    if (v === 'yes_help') {
      navigate('/tradeincontact');
      return;
    }

    if (!customerUuid) {
      setSaveError(t.tradeIn.sessionExpiredError);
      return;
    }

    const digits = (mobileLocalDigits ?? '').replace(/\D/g, '');
    if (digits.length === 0) {
      setSaveError(t.tradeIn.mobileError);
      return;
    }

    const body = buildSurveySubmitBodyFromStorage({
      ...TNG_SKIP_CONTACT,
      mobileLocalDigits: digits,
    });
    if (!body) {
      setSaveError(t.tradeIn.incompleteError);
      return;
    }

    setSavingTng(true);
    void submitCustomerSurveyTng(customerUuid, body)
      .then((result) => {
        writeTngRewardSession({
          pin: result.pin,
          expirydate: result.expirydate,
          value: result.value,
        });
        clearAllLocalStorage();
        navigate('/tng', { replace: true });
      })
      .catch((err: unknown) => {
        setSaveError(err instanceof Error ? err.message : 'Unable to save. Please try again.');
      })
      .finally(() => {
        setSavingTng(false);
      });
  };

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!pdpaConsent) {
    return <Navigate to="/pdpa" replace />;
  }
  if (!mobileLocalDigits || mobileLocalDigits.replace(/\D/g, '').length === 0) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!isSmokerProfileComplete()) {
    return <Navigate to="/smokerprofile" replace />;
  }
  if (!isQ7_1Complete()) {
    return <Navigate to="/7_1" replace />;
  }
  if (!isQ7_2Complete()) {
    return <Navigate to="/7_2" replace />;
  }
  if (!isQ7_3Complete()) {
    return <Navigate to="/7_3" replace />;
  }
  if (!isQ7_4CompleteForSurvey()) {
    return <Navigate to="/7_4" replace />;
  }
  if (!isDevRegComplete()) {
    return <Navigate to="/devreg" replace />;
  }

  return (
    <SurveyBackgroundLayout>
      {/* No overflow-x clip here — negative horizontal margin must bleed into layout `px-6`. */}
      <div className="relative z-10 flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="-mx-6 shrink-0 bg-black">
            <img
              src="/images/tradein.png"
              alt=""
              className="m-0 block h-auto w-full min-w-0 max-w-none"
            />
          </div>
          <div className="flex min-h-0 min-w-0 flex-1 flex-col justify-center overflow-y-auto">
            <div className="mx-auto flex w-full max-w-full flex-col py-4">
              <SurveyBrownPanelInset>
                <SurveyBrownPanel className="!p-4">
                  <p className="m-0 text-left text-[16px] font-medium leading-snug text-white/95 sm:text-[17px]">
                    {t.tradeIn.question}
                  </p>
                  <div className="mt-5 flex flex-col gap-3">
                    {OPTION_KEYS.map((opt) => {
                      const id = `tradein-${opt.id}`;
                      const checked = choice === opt.id;
                      return (
                        <label key={opt.id} htmlFor={id} className={checkboxLabelClass}>
                          <input
                            id={id}
                            type="checkbox"
                            checked={checked}
                            disabled={savingTng}
                            onChange={() => pick(opt.id)}
                            className={checkboxClass}
                          />
                          <span>{t.tradeIn[opt.labelKey]}</span>
                        </label>
                      );
                    })}
                  </div>
                  {saveError != null ? (
                    <div className="mt-4">
                      <SurveyValidationAlert>{saveError}</SurveyValidationAlert>
                    </div>
                  ) : null}
                  <p className="mt-6 m-0 text-left text-[13px] italic leading-snug text-white/80 sm:text-[14px]">
                    {t.tradeIn.tnc}
                  </p>
                </SurveyBrownPanel>
              </SurveyBrownPanelInset>
            </div>
          </div>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
